import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import StyledButtonV2 from "../../../../../../../../components/app/StyledButtonV2";
import AsyncSelect, {
  AsyncSelectOption,
} from "../../../../../../../../components/AsyncSelect";
import CustomSelect from "../../../../../../../../components/CustomSelect";
import Flex from "../../../../../../../../components/Flex";
import StyledFormModal from "../../../../../../../../components/globalModal/StyledModal";
import ProviderSelectScroll from "../../../../../../../../components/ProviderSelectScroll";
import Text from "../../../../../../../../components/Text";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useConfiguredClearinghouses } from "../../../../../../../../queries/billings/claim-integration/use-configured-clearinghouses";
import { useCheckEligibility } from "../../../../../../../../queries/eligibility/use-check-eligibility";
import { useGetInsuranceByPatientId } from "../../../../../../../../queries/insurance/use-get-patient-insurance-patient-uuid";
import { CheckEligibilityVendorEnum } from "../../../../../../../../services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../../../../utils/utils";
import { VENDOR_TYPE_LIST } from "../../../../../settings/claim-integration/CreateClaimIntegrationDrower";
import EligibilityOverviewStatus from "./EligibilityOverviewStatus";
import EligibilityResponseModal from "./eligibilityResponseModal";

/**
 * The prefill is built from a `PatientInsurance`, whose fields are all optional
 * on the generated model, so every member here can genuinely be absent.
 */
type Ins = {
  insuranceId?: string;
  label?: string;
  value?: string;
};

interface CheckEligibilityModalProps {
  disclosureHost: DisclosureHost;
  eligibilityReportDisclosure: DisclosureHost;
  onEligibilityChecked?: () => void;
  prefillIns?: Ins;
  prefillProvider?: AsyncSelectOption;
}

const CheckEligibilityModal = ({
  disclosureHost,
  eligibilityReportDisclosure,
  onEligibilityChecked,
  prefillIns,
  prefillProvider,
}: CheckEligibilityModalProps) => {
  const { patientId: patientIdFromParams } = useParams();
  const patientId =
    (disclosureHost.data as string) || (patientIdFromParams as string);
  const {
    data: patientInsuraneData,
    isLoading: isLoadingInsurance,
    isFetching: isFetchingInsurance,
  }: any = useGetInsuranceByPatientId(patientId);

  const { mutateAsync: checkEligibility } = useCheckEligibility();
  // const [eligibilityResponseData, setEligibilityResponseData] = useState<any>();
  const eligibilityResponseDisclosure = useDisclosure(false);
  const showSnackbar = useSnackbar();

  // ECRH-5271: only show vendors that are actually configured, and auto-select
  // the vendor when exactly one clearinghouse is configured. When multiple (or
  // none) are configured, singleVendor is null and the dropdown is left for
  // manual selection.
  const { vendors, singleVendor } = useConfiguredClearinghouses();
  const vendorOptions = VENDOR_TYPE_LIST.filter((it) =>
    vendors.includes(it.value as (typeof vendors)[number])
  );

  const initialValue = {
    insurance: prefillIns ?? undefined,
    provider: prefillProvider ?? undefined,
    vendor: singleVendor ?? undefined,
    // serviceType: undefined,
    // dateOfService: undefined,
  };

  const validationSchema = Yup.object({
    insurance: Yup.object().required().label("Insurance"),
    // provider: Yup.object().optional().label("Provider"),
    vendor: Yup.string().required().label("Vendor"),
    // serviceType: Yup.string().required().label("Service Type"),
    // dateOfService: Yup.date().required().label("Date Of Service"),
  });

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const [fetchedEligibility, setFetchedEligibility] = useState(false);
  // ECRH-5111 #10: track failures so the user can explicitly retry a failed
  // eligibility transaction without re-entering the form.
  const [checkFailed, setCheckFailed] = useState(false);

  async function handleSubmit(values?: any) {
    setCheckFailed(false);
    checkEligibility({
      patientUuid: patientId,
      insuranceId: values.insurance.insuranceId,
      vendor: values.vendor as CheckEligibilityVendorEnum,
      providerId: values.provider.value,
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        if (onEligibilityChecked) {
          onEligibilityChecked();
          closeAndResetForm();
        } else {
          eligibilityResponseDisclosure.open(res.data);
        }
      })
      .catch((err) => {
        setCheckFailed(true);
        showSnackbar(getErrorSnackbar(err));
      });
  }

  function closeAndResetForm() {
    formik.resetForm();
    setCheckFailed(false);
    disclosureHost.close();
  }

  return (
    <>
      {eligibilityResponseDisclosure.opened && (
        <EligibilityResponseModal
          disclosureHost={eligibilityResponseDisclosure}
        />
      )}
      <StyledFormModal
        open={disclosureHost.opened}
        onClose={closeAndResetForm}
        dialogTitle="Check Eligibility"
        {...(!fetchedEligibility && {
          dialogAction: (
            <Flex justifyContent={"end"} gap={1}>
              <StyledButtonV2
                label="Cancel"
                variant="outlined"
                onClick={closeAndResetForm}
              />
              <StyledButtonV2
                label={checkFailed ? "Retry" : "Check Eligibility"}
                variant="contained"
                onClick={formik.submitForm}
                sx={{ width: "150px" }}
              />
            </Flex>
          ),
        })}
        sx={{
          "& .MuiDialog-paper": {
            width: "30vw",
            height: "80vh",
          },
        }}
      >
        <Box pt={2}>
          {!fetchedEligibility ? (
            <Box display={"flex"} flexDirection={"column"} gap={2} width="100%">
              {checkFailed && (
                <Box bgcolor="#FFF2F3" borderRadius={1} p={1.5} width="100%">
                  <Text fontSize="12px" color="#B1000F" fontWeight={500}>
                    The eligibility check failed. Please verify the details and
                    click Retry.
                  </Text>
                </Box>
              )}
              {/* Eligibility Last Checked Section */}
              {/* <Box
                bgcolor="#EEF7FF"
                borderRadius={1}
                p={2}
                display="flex"
                flexDirection="column"
                gap={0.5}
                width="100%"
              >
                <Text fontSize="12px" color="#595F63" fontWeight={400}>
                  Eligibility Last Checked
                </Text>
                <Flex alignItems="center" gap={3}>
                  <Text fontSize="12px" color="#21262B" fontWeight={500}>
                    Not Available
                  </Text>
                </Flex>
              </Box> */}
              <Box mt={1}>
                <Grid item container spacing={2.5}>
                  <Grid item xs={12}>
                    <CustomSelect
                      label="Vendor Type"
                      required
                      name="vendor"
                      placeholder="Select Vendor Type"
                      options={vendorOptions}
                      value={formik.values.vendor || undefined}
                      onChange={(val) => {
                        formik.setFieldValue("vendor", val);
                      }}
                      error={
                        formik.touched.vendor && (formik.errors.vendor as any)
                      }
                    />
                  </Grid>
                  {/* Insurance  */}
                  <Grid item xs={12}>
                    <AsyncSelect
                      required
                      loading={isLoadingInsurance ?? isFetchingInsurance}
                      label="Insurance"
                      name="insurance"
                      options={patientInsuraneData?.content?.map((it: any) => ({
                        value: it?.uuid,
                        label: `${it?.insurancePayer?.payerName}`,
                        insuranceId: it?.insuranceId,
                      }))}
                      enableFilterOption
                      renderCustomLabel={(option) => (
                        <Flex flexDirection={"column"}>
                          <Text>{option.label}</Text>
                        </Flex>
                      )}
                      placeholder="Search & Select Insurance"
                      value={formik.values.insurance}
                      onChange={(val) => {
                        if (val && !val.insuranceId) {
                          showSnackbar({
                            type: "error",
                            message:
                              "Selected insurance is missing Insurance ID. Please update insurance details and try again.",
                          });
                          formik.setFieldValue("insurance", undefined);
                          return;
                        }
                        formik.setFieldValue("insurance", val);
                      }}
                      error={
                        formik.touched.insurance &&
                        (formik.errors.insurance as any)
                      }
                    />
                  </Grid>
                  {/* Provider */}
                  <Grid item xs={12}>
                    <ProviderSelectScroll
                      label="Provider"
                      value={formik.values.provider || null}
                      onChange={(val) => formik.setFieldValue("provider", val)}
                      handleBlur={formik.handleBlur}
                      name="provider"
                      error={
                        (formik.touched?.provider &&
                          formik.errors?.provider) as any
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ) : (
            <EligibilityOverviewStatus
              title="Eligibility Overview"
              description="The patient is eligible for the service"
              redirectToViewReport={true}
              onViewReport={() => {
                eligibilityReportDisclosure.open();
                setFetchedEligibility(false);
              }}
              onClose={() => {
                setFetchedEligibility(false);
              }}
            />
          )}
        </Box>
      </StyledFormModal>
    </>
  );
};

export default CheckEligibilityModal;
